/**
 * DomainKnowledgeGuidance
 *
 * Provides context-aware guidance for Content Managers on how to use
 * the Domain Knowledge system, with examples tailored to their sector
 * and target personas.
 *
 * Date: January 7, 2026
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Lightbulb,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Sparkles,
  AlertTriangle,
  FlaskConical,
  Users,
  Target,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

interface Persona {
  id: string;
  persona_name: string;
  description: string;
  age_range?: string;
  occupation?: string;
  pain_points?: string;
  expectations?: string;
  priorities?: string[];
}

interface Sector {
  id: number;
  name: string;
  description?: string;
}

interface SectorData {
  sectors: Sector[];
  primarySectorId: number | null;
  personas: Persona[];
}

// Examples mapped to common sectors
const SECTOR_EXAMPLES: Record<string, {
  knowledge: string[];
  opportunities: string[];
  staleContent: string;
}> = {
  'cycling': {
    knowledge: [
      '"Shimano 105 groupset weighs 2.5kg" - helps AI write accurate specifications',
      '"Carbon frames require torque wrench for assembly" - adds expert safety advice',
      '"Tubeless tires need sealant refresh every 6 months" - maintenance knowledge',
    ],
    opportunities: [
      'Missing "frame material" for 45 products - research carbon vs aluminum benefits',
      'Missing "groupset compatibility" for premium bikes',
    ],
    staleContent: 'When bike industry standards change (like new SRAM groupsets), regenerate affected product content.',
  },
  'beauty': {
    knowledge: [
      '"Retinol should not be used with vitamin C" - ingredient interaction knowledge',
      '"SPF 30 blocks 97% of UVB rays" - factual skincare data',
      '"Hyaluronic acid works best on damp skin" - application expertise',
    ],
    opportunities: [
      'Missing "skin type suitability" for 120 products',
      'Missing "active ingredients percentage" for serums',
    ],
    staleContent: 'When new research emerges about ingredient efficacy, update affected product descriptions.',
  },
  'electronics': {
    knowledge: [
      '"USB-C PD supports up to 240W with EPR" - technical specification',
      '"OLED displays have infinite contrast ratio" - display technology facts',
      '"Wi-Fi 6E operates on 6GHz band" - connectivity standards',
    ],
    opportunities: [
      'Missing "processor generation" for 80 laptops',
      'Missing "refresh rate" for monitors',
    ],
    staleContent: 'When new product versions release, regenerate content for previous generation products.',
  },
  'default': {
    knowledge: [
      'Product specifications that help AI write accurate descriptions',
      'Industry terminology and what it means for customers',
      'Expert tips that add value to product content',
    ],
    opportunities: [
      'Missing attributes that would improve product descriptions',
      'Specification gaps that make content less helpful',
    ],
    staleContent: 'When industry standards or product knowledge changes, affected content needs regeneration.',
  },
};

// Get sector-specific examples
function getSectorExamples(sectorName: string | undefined): typeof SECTOR_EXAMPLES['default'] {
  if (!sectorName) return SECTOR_EXAMPLES['default'];

  const normalizedName = sectorName.toLowerCase();

  if (normalizedName.includes('cycling') || normalizedName.includes('bike') || normalizedName.includes('bicycle')) {
    return SECTOR_EXAMPLES['cycling'];
  }
  if (normalizedName.includes('beauty') || normalizedName.includes('cosmetic') || normalizedName.includes('skincare')) {
    return SECTOR_EXAMPLES['beauty'];
  }
  if (normalizedName.includes('electronic') || normalizedName.includes('tech') || normalizedName.includes('computer')) {
    return SECTOR_EXAMPLES['electronics'];
  }

  return SECTOR_EXAMPLES['default'];
}

interface DomainKnowledgeGuidanceProps {
  activeTab: string;
}

export function DomainKnowledgeGuidance({ activeTab }: DomainKnowledgeGuidanceProps) {
  const { selectedSite } = useSite();
  const [isOpen, setIsOpen] = useState(true);
  const [sectorData, setSectorData] = useState<SectorData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedSite?.id) {
      loadSectorData();
    }
  }, [selectedSite?.id]);

  const loadSectorData = async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    try {
      const response = await authFetch(`/api/sites/${selectedSite.id}/sectors`);
      const data = await response.json();
      if (data.success) {
        setSectorData(data);
      }
    } catch (err) {
      console.error('[DomainKnowledgeGuidance] Error loading sector data:', err);
    } finally {
      setLoading(false);
    }
  };

  const primarySector = sectorData?.sectors.find(s => s.id === sectorData.primarySectorId);
  const examples = getSectorExamples(primarySector?.name);
  const personas = sectorData?.personas || [];

  // Tab-specific guidance content
  const getTabGuidance = () => {
    switch (activeTab) {
      case 'knowledge':
        return {
          icon: BookOpen,
          title: 'Knowledge Base',
          description: 'Add facts, specifications, and expert knowledge that the AI uses when writing product content.',
          tips: [
            'Add specific facts about your products that might not be in the feed',
            'Include industry terminology with explanations',
            'Add expert tips that differentiate your content from competitors',
          ],
          examples: examples.knowledge,
        };

      case 'opportunities':
        return {
          icon: Sparkles,
          title: 'Enrichment Opportunities',
          description: 'See which product attributes are missing and could improve your content quality.',
          tips: [
            'Review missing attributes grouped by category and price tier',
            'Prioritize high-value products or categories with most gaps',
            'Use "Ask AI" to research missing information in batches',
          ],
          examples: examples.opportunities,
        };

      case 'staleness':
        return {
          icon: AlertTriangle,
          title: 'Stale Content Queue',
          description: 'Review content that may be outdated due to knowledge updates.',
          tips: [
            'When you update knowledge, affected products are queued here',
            'Review regenerated content and validate if it improved',
            'Dismiss items that don\'t need regeneration',
          ],
          examples: [examples.staleContent],
        };

      case 'research':
        return {
          icon: FlaskConical,
          title: 'Research Queue',
          description: 'Track AI research tasks for filling knowledge gaps.',
          tips: [
            'Items here are waiting for AI to research and fill in gaps',
            'You can manually process items or let the system auto-process',
            'Review completed research before it becomes active knowledge',
          ],
          examples: ['Research items are created when knowledge gaps are detected during content generation.'],
        };

      default:
        return null;
    }
  };

  const tabGuidance = getTabGuidance();

  if (!tabGuidance) return null;

  const TabIcon = tabGuidance.icon;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-6">
      <Card className="border-blue-200 bg-blue-50/50">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-blue-50/80 transition-colors py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-blue-600" />
                <span className="text-blue-900">How to use this section</span>
                {primarySector && (
                  <Badge variant="outline" className="text-xs bg-white">
                    {primarySector.name}
                  </Badge>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-blue-600" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-blue-600" />
                )}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-4">
            <div className="space-y-4">
              {/* Tab-specific description */}
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <TabIcon className="h-5 w-5 text-blue-700" />
                </div>
                <div>
                  <h4 className="font-medium text-blue-900">{tabGuidance.title}</h4>
                  <p className="text-sm text-blue-800/80 mt-1">{tabGuidance.description}</p>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-white/60 rounded-lg p-3 space-y-2">
                <h5 className="text-xs font-medium text-blue-900 uppercase tracking-wide">What you should do:</h5>
                <ul className="space-y-1.5">
                  {tabGuidance.tips.map((tip, i) => (
                    <li key={i} className="text-sm text-blue-800 flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Sector-specific examples */}
              {tabGuidance.examples.length > 0 && (
                <div className="bg-white/60 rounded-lg p-3 space-y-2">
                  <h5 className="text-xs font-medium text-blue-900 uppercase tracking-wide flex items-center gap-2">
                    <Target className="h-3 w-3" />
                    Examples for your sector:
                  </h5>
                  <ul className="space-y-1.5">
                    {tabGuidance.examples.map((example, i) => (
                      <li key={i} className="text-sm text-blue-800 flex items-start gap-2">
                        <span className="text-blue-400 mt-1 font-mono text-xs">{i + 1}.</span>
                        <span className="italic">{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Personas - show who you're creating content for */}
              {personas.length > 0 && activeTab === 'knowledge' && (
                <div className="bg-white/60 rounded-lg p-3 space-y-2">
                  <h5 className="text-xs font-medium text-blue-900 uppercase tracking-wide flex items-center gap-2">
                    <Users className="h-3 w-3" />
                    Your target personas (keep them in mind):
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {personas.slice(0, 4).map((persona) => (
                      <div
                        key={persona.id}
                        className="bg-blue-100/50 rounded-md px-2.5 py-1.5 text-xs"
                        title={persona.description}
                      >
                        <span className="font-medium text-blue-900">{persona.persona_name}</span>
                        {persona.occupation && (
                          <span className="text-blue-700 ml-1">({persona.occupation})</span>
                        )}
                      </div>
                    ))}
                    {personas.length > 4 && (
                      <span className="text-xs text-blue-600 self-center">+{personas.length - 4} more</span>
                    )}
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    Add knowledge that would help these personas make better purchasing decisions.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default DomainKnowledgeGuidance;
